// Изменение тарифа с даты. Панель с историей изменений внизу — тариф это не поле, а протокол.
//
// ⛔ ОСНОВАНИЕ ОБЯЗАТЕЛЬНО. Это прямая просьба заказчика: «хорошо бы иметь примечание с
// указанием причины, чтобы не вспоминать». Сервер без основания запись не сохранит
// (RateChangeIn.basis, minLength 1), и здесь проверка стоит не вместо серверной, а раньше
// неё — чтобы человек узнал об этом до отправки, а не отказом после.
//
// Ставку по-прежнему не вводят числом нигде, кроме режима «прямая ставка»: см. заголовок
// components/RateFields.tsx и число 5 044 в server/app/domain/payroll/rate.py.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import type { RateChangeIn } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { formatHours } from '@/shared/hours/format';
import { formatRateRubles, formatRateWholeRubles, formatRubles } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import type { RateFieldName } from '../components/RateFields';
import { EMPTY_RATE_FORM, RateFields, buildRateValues } from '../components/RateFields';
import { rateModeTitle } from '../dictionaries';
import { newIdempotencyKey, useRateChangeMutation, useRateChangesQuery } from '../queries';

/** Изменение тарифа — отдельное право: server/app/api/v1/assignments.py. */
const RATE_PERMISSION = 'core.rate.edit';

type FieldName = 'effective_from' | 'basis' | RateFieldName;

export function RateChangePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, RATE_PERMISSION);

  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);
  const [basis, setBasis] = useState('');
  const [rate, setRate] = useState(EMPTY_RATE_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  const history = useRateChangesQuery(id, can(permissions, 'core.rate.view'));
  const mutation = useRateChangeMutation(id, idempotencyKey);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Менять тариф может тот, у кого есть право на тарифы. Это отдельное право: деньги двигает не тот же человек, что ведёт карточки. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Изменение уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (effectiveFrom === '') {
      next.effective_from = 'Укажите, с какого дня действует новый тариф.';
    }
    // Основание обязательно — и здесь, и на сервере. Без него запись не сохраняется.
    if (basis.trim() === '') {
      next.basis =
        'Основание обязательно: через полгода никто не вспомнит, почему тариф изменился, а в ведомости это будут видеть все.';
    }
    const rateValues = buildRateValues(rate);
    if (!rateValues.ok) {
      Object.assign(next, rateValues.errors);
    }
    if (Object.keys(next).length > 0 || !rateValues.ok) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: RateChangeIn = {
      effective_from: effectiveFrom,
      basis: basis.trim(),
      ...rateValues.values,
    };
    mutation.mutate(body, {
      onSuccess: () => {
        setIdempotencyKey(newIdempotencyKey());
        onClose();
      },
    });
  }

  return (
    <div className='flex flex-col gap-4'>
      <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
        {!allowed && (
          <Alert.Root variant='light' status='warning' size='small' role='status'>
            <Alert.Icon as={RiErrorWarningLine} />
            <span>
              Права на тарифы у вас нет: форму можно заполнить, но изменение не сохранится. Это
              отдельное право, его выдаёт администратор.
            </span>
          </Alert.Root>
        )}

        <DateField
          id='rate-effective-from'
          label='Действует с'
          required
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          error={errors.effective_from}
          hint='Прежний тариф действует до этого дня: расчёт за прошлые периоды не поедет.'
        />

        <RateFields idPrefix='rate' value={rate} onChange={setRate} errors={errors} />

        <TextField
          id='rate-basis'
          label='Основание изменения'
          required
          value={basis}
          onChange={setBasis}
          placeholder='Например: повышение по итогам вахты, приказ № 14'
          error={errors.basis}
          hint='Обязательно. Останется в истории тарифа рядом со старым и новым значением.'
        />

        {mutation.isError && <ErrorAlert error={mutation.error} />}

        {gateNote !== null && (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {gateNote}
          </Hint.Root>
        )}

        <div className='flex items-center gap-2 pt-1'>
          <Button.Root type='submit' variant='primary' mode='filled' size='small'>
            {mutation.isPending ? 'Сохраняем…' : 'Изменить тариф'}
          </Button.Root>
          <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
            Отмена
          </Button.Root>
        </div>
      </form>

      <div className='flex flex-col gap-2'>
        <span className='text-label-sm text-text-strong-950'>История тарифа</span>
        <ScreenState
          data={history.data}
          error={history.error}
          subject='история тарифа'
          isEmpty={(data) => data.items.length === 0}
          onRetry={() => void history.refetch()}
          loading={<Skeleton className='h-16 w-full' />}
          empty={
            <p className='text-paragraph-sm text-text-sub-600'>
              Тариф ни разу не менялся: действует тот, что задали при постановке на объект.
            </p>
          }
        >
          {(data) => (
            <ol className='flex flex-col rounded-xl bg-bg-white-0 px-4 ring-1 ring-inset ring-stroke-soft-200'>
              {data.items.map((change) => (
                <li
                  key={change.id}
                  className='flex flex-col gap-0.5 border-b border-stroke-soft-200 py-3 last:border-b-0'
                >
                  <span className='text-label-sm text-text-strong-950'>
                    с {formatDateOnly(change.effective_from)} · {rateModeTitle(change.rate_mode)}
                  </span>
                  <span className='text-paragraph-xs text-text-sub-600'>
                    ставка {formatRateWholeRubles(change.old_hourly_rate)} →{' '}
                    {formatRateRubles(change.new_hourly_rate)}/час
                    {change.new_period_amount !== null &&
                      `, сумма за период ${formatRubles(change.new_period_amount)}`}
                    {/* Норма — тоже часть тарифа. Без неё смена «75 000 при 372 → 75 000 при 360»
                        читалась строкой «сумма за период 75 000», то есть как «ничего не
                        поменяли» (13.10.2026). Прежней нормы у старых записей может не быть. */}
                    {change.new_norm_hours !== null &&
                      change.new_norm_hours !== undefined &&
                      `, норма ${
                        change.old_norm_hours !== null &&
                        change.old_norm_hours !== undefined &&
                        change.old_norm_hours !== change.new_norm_hours
                          ? `${formatHours(change.old_norm_hours)} → `
                          : ''
                      }${formatHours(change.new_norm_hours)} ч`}
                  </span>
                  <span className='text-paragraph-xs text-text-sub-600'>
                    {change.basis === null ||
                    change.basis === undefined ||
                    change.basis.trim() === ''
                      ? 'основание не записано'
                      : `основание: ${change.basis}`}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </ScreenState>
      </div>
    </div>
  );
}
